import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AuthenticationService } from 'app/authentication/service';
import { ChatConversationAPIService } from './chat-conversation.service';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
export class ChatRealtimeService {
  private readonly conversationsSubject = new BehaviorSubject<any[]>([]);
  private readonly activeConversationIdSubject = new BehaviorSubject<string | null>(null);

  private authSubscription: Subscription | null = null;
  private socketSubscriptions = new Subscription();
  private partnerConversationsSub: Subscription | null = null;
  private joinedConversationIds = new Set<string>();
  private selectedPartnerId: string | null = null;
  private sessionKey: string | null = null;
  private initialized = false;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly chatConversationApi: ChatConversationAPIService,
    private readonly socketService: SocketService
  ) {}

  get conversations$() {
    return this.conversationsSubject.asObservable();
  }

  get conversationsValue(): any[] {
    return this.conversationsSubject.value;
  }

  get activeConversationId(): string | null {
    return this.activeConversationIdSubject.value;
  }

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.authSubscription = this.authService.currentUser.subscribe((user) => {
      this.handleUserSessionChange(user);
    });
  }

  async setSelectedPartner(partnerId: string): Promise<void> {
    this.selectedPartnerId = partnerId;
    this.conversationsSubject.next([]);
    this.joinedConversationIds.clear();
    await this.requestPartnerConversations(partnerId);
  }

  setActiveConversation(conversationId: string | null): void {
    const cid = conversationId == null ? null : this.normalizeEntityId(conversationId);
    this.activeConversationIdSubject.next(cid);
    if (!cid) {
      return;
    }
    this.ensureConversationJoined(cid);
    const list = this.conversationsValue.map((conversation) =>
      this.normalizeEntityId(conversation._id) === cid
        ? { ...conversation, unreadCount: 0 }
        : conversation
    );
    this.conversationsSubject.next(list);
  }

  async markConversationRead(conversationId: string, userId: string): Promise<void> {
    await this.chatConversationApi.readMessagesByConversationId(conversationId, userId);
    const markId = this.normalizeEntityId(conversationId);
    const list = this.conversationsValue.map((conversation) =>
      this.normalizeEntityId(conversation._id) === markId
        ? { ...conversation, unreadCount: 0 }
        : conversation
    );
    this.conversationsSubject.next(list);
  }

  private handleUserSessionChange(user: any): void {
    if (!user) {
      this.resetSession();
      return;
    }

    const token = user.partnerToken || user.accessToken;
    if (!token) {
      this.resetSession();
      return;
    }

    const newSessionKey = `${user._id || ''}:${token}:${user.loginType || 'partner'}`;
    if (newSessionKey === this.sessionKey) {
      return;
    }

    this.sessionKey = newSessionKey;
    this.selectedPartnerId = null;
    this.joinedConversationIds.clear();
    this.conversationsSubject.next([]);
    this.activeConversationIdSubject.next(null);

    this.socketService.connectWithToken(token);
    this.registerSocketListeners();
    this.bootstrapConversations(user);
  }

  private async bootstrapConversations(user: any): Promise<void> {
    if (!user?._id) {
      return;
    }

    // Admin monitors partner conversations on demand.
    if (user.partnerType === 'admin' || user.partnerType === 'super-admin') {
      return;
    }

    // Producer and staff partner sessions are partner-scoped.
    if (user.partnerType === 'producer' || user.loginType === 'staff') {
      await this.requestPartnerConversations(user._id);
      return;
    }

    // Normal users are scoped to their own user conversations.
    try {
      const response: any = await this.chatConversationApi.getByUserId(user._id);
      this.replaceConversations(response?.results || []);
    } catch (error) {
      this.conversationsSubject.next([]);
    }
  }

  private registerSocketListeners(): void {
    this.socketSubscriptions.unsubscribe();
    this.socketSubscriptions = new Subscription();

    this.socketSubscriptions.add(
      this.socketService.onNewMessage().subscribe((message) => this.handleIncomingMessage(message))
    );
    this.socketSubscriptions.add(
      this.socketService.onConversationOpened().subscribe((conversation) =>
        this.handleConversationOpened(conversation)
      )
    );
    this.attachPartnerConversationsListener();
  }

  private attachPartnerConversationsListener(): void {
    this.partnerConversationsSub?.unsubscribe();
    this.partnerConversationsSub = this.socketService
      .onPartnerConversations()
      .subscribe((payload) => this.replaceConversations(payload?.results || payload || []));
  }

  private async requestPartnerConversations(partnerId: string): Promise<void> {
    if (!partnerId) {
      return;
    }
    this.socketService.getPartnerConversations(partnerId);
  }

  private handleIncomingMessage(message: any): void {
    const convMsgId = this.normalizeEntityId(message?.conversationId);
    if (!convMsgId) {
      return;
    }

    const currentUserId = this.authService.currentUserValue?._id;
    const activeId = this.activeConversationIdSubject.value;
    const activeIdStr = activeId == null ? '' : this.normalizeEntityId(activeId);
    const isActiveConversation = activeIdStr === convMsgId;
    const senderStr = this.normalizeEntityId(message?.sender);
    const isOwnMessage = currentUserId && senderStr === this.normalizeEntityId(currentUserId);

    const list = [...this.conversationsValue];
    const index = list.findIndex(
      (conversation) => this.normalizeEntityId(conversation?._id) === convMsgId
    );
    if (index === -1) {
      return;
    }

    const updated = { ...list[index] };
    updated.lastMessage = message;
    if (!isActiveConversation && !isOwnMessage) {
      updated.unreadCount = (updated.unreadCount || 0) + 1;
    }

    list.splice(index, 1);
    list.unshift(updated);
    this.conversationsSubject.next(list);
  }

  private handleConversationOpened(conversation: any): void {
    if (!conversation?._id || !this.canCurrentUserSeeConversation(conversation)) {
      return;
    }
    this.upsertConversation(conversation);
  }

  private canCurrentUserSeeConversation(conversation: any): boolean {
    const currentUser = this.authService.currentUserValue;
    if (!currentUser) {
      return false;
    }

    const conversationPartnerId =
      conversation?.partnerId?._id || conversation?.partnerId?.toString?.() || conversation?.partnerId;
    const conversationUserId =
      conversation?.userId?._id || conversation?.userId?.toString?.() || conversation?.userId;

    if (currentUser.partnerType === 'admin' || currentUser.partnerType === 'super-admin') {
      return this.selectedPartnerId
        ? String(conversationPartnerId) === String(this.selectedPartnerId)
        : false;
    }

    if (currentUser.partnerType === 'producer' || currentUser.loginType === 'staff') {
      return String(conversationPartnerId) === String(currentUser._id);
    }

    return String(conversationUserId) === String(currentUser._id);
  }

  private replaceConversations(conversations: any[]): void {
    const sorted = [...(conversations || [])].sort(
      (a, b) =>
        new Date(b?.lastMessage?.createdAt || 0).getTime() -
        new Date(a?.lastMessage?.createdAt || 0).getTime()
    );
    this.conversationsSubject.next(sorted);
    sorted.forEach((conversation) =>
      this.ensureConversationJoined(this.normalizeEntityId(conversation?._id))
    );
  }

  private upsertConversation(conversation: any): void {
    const list = [...this.conversationsValue];
    const index = list.findIndex((item) => item._id === conversation._id);
    if (index >= 0) {
      list.splice(index, 1);
    }
    list.unshift({ ...conversation, unreadCount: conversation.unreadCount || 0 });
    this.conversationsSubject.next(list);
    this.ensureConversationJoined(this.normalizeEntityId(conversation._id));
  }

  private ensureConversationJoined(conversationId: unknown): void {
    const cid = this.normalizeEntityId(conversationId);
    if (!cid || this.joinedConversationIds.has(cid)) {
      return;
    }
    this.joinedConversationIds.add(cid);
    this.socketService.joinConversation(cid);
  }

  /** Single string id for sockets + Mongo payloads (handles ObjectId / $oid / populated refs). */
  private normalizeEntityId(raw: unknown): string {
    if (raw === null || raw === undefined) {
      return '';
    }
    if (typeof raw === 'string') {
      return raw;
    }
    if (typeof raw === 'object') {
      const o = raw as { _id?: unknown; $oid?: string; toString?: () => string };
      if (o._id !== undefined && o._id !== raw) {
        return this.normalizeEntityId(o._id);
      }
      if (typeof o.$oid === 'string') {
        return o.$oid;
      }
      const s =
        typeof o.toString === 'function' ? String(o.toString()) : '';
      return s && s !== '[object Object]' ? s : '';
    }
    return String(raw);
  }

  private resetSession(): void {
    this.sessionKey = null;
    this.selectedPartnerId = null;
    this.joinedConversationIds.clear();
    this.activeConversationIdSubject.next(null);
    this.conversationsSubject.next([]);
    this.socketSubscriptions.unsubscribe();
    this.socketSubscriptions = new Subscription();
    this.partnerConversationsSub?.unsubscribe();
    this.partnerConversationsSub = null;
    this.socketService.disconnect();
  }
}
